import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Button } from '../components/ui/button';
import { adminApi } from '../utils/api';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight } from 'lucide-react';

export const Logs = ({ adminSecret }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  useEffect(() => {
    fetchLogs();
  }, [page]);
  
  const fetchLogs = async () => {
    setLoading(true);
    try {
      const response = await adminApi.getLogs(adminSecret, null, page, 50);
      setLogs(response.data.logs);
      setTotalPages(response.data.pages);
    } catch (error) {
      toast.error('Failed to load logs');
    } finally {
      setLoading(false);
    }
  };
  
  const getStatusColor = (status) => {
    if (status >= 200 && status < 300) return 'text-emerald-600 bg-emerald-50';
    if (status >= 400 && status < 500) return 'text-orange-600 bg-orange-50';
    if (status >= 500) return 'text-red-600 bg-red-50';
    return 'text-gray-600 bg-gray-50';
  };
  
  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading logs...</div>;
  }
  
  return (
    <div className="space-y-6" data-testid="logs-page">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Request Logs</h1>
        <p className="text-gray-600 mt-2">Monitor API usage and requests</p>
      </div>
      
      <Card>
        <CardHeader>
          <CardTitle>Recent Requests</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Timestamp</TableHead>
                <TableHead>User</TableHead>
                <TableHead>Method</TableHead>
                <TableHead>Endpoint</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Response Time</TableHead>
                <TableHead>IP Address</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {logs.map((log, index) => (
                <TableRow key={index} data-testid={`log-row-${index}`}>
                  <TableCell className="text-sm">
                    {new Date(log.timestamp).toLocaleString()}
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">
                      <p className="font-medium">{log.user_email || 'Unknown'}</p>
                      <code className="text-xs text-gray-500">
                        {log.api_key.substring(0, 12)}...
                      </code>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="font-medium text-sm">{log.method}</span>
                  </TableCell>
                  <TableCell className="text-sm font-mono">{log.endpoint}</TableCell>
                  <TableCell>
                    <span
                      className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                        getStatusColor(log.status_code)
                      }`}
                    >
                      {log.status_code}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm">
                    {log.response_time_ms.toFixed(2)}ms
                  </TableCell>
                  <TableCell className="text-sm text-gray-500">
                    {log.ip_address}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          
          {/* Pagination */}
          <div className="flex items-center justify-between mt-4">
            <div className="text-sm text-gray-600">
              Page {page} of {totalPages}
            </div>
            <div className="flex space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                data-testid="prev-page-button"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                data-testid="next-page-button"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};